import {
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMemo, useState } from "react";
import type { ModuleBuilderDraft, QuestionOverride, ReadinessItem } from "./types";
import type { ParsedActivityRow } from "./catalogModel";
import {
  answersToOptionsText,
  getQuestionDisplayMeta,
  parseOptionsText
} from "./catalogModel";
import type { FlatLibraryQuestion } from "./catalogTypes";

type SourceActivity = { id: string; title: string; description: string | null };

type Props = {
  draft: ModuleBuilderDraft;
  parsedByInstrumentId: Record<string, ParsedActivityRow[]>;
  /** Map from a sourceAssessmentId to its existing activity rows. Used to
   *  compute per-row reuse highlighting in the preview. */
  sourceActivitiesByAssessmentId: Record<string, SourceActivity[]>;
  /** Per-draft-instrument list of inherited questions (when sourceInstrumentId
   *  is set and the detail has loaded). Empty for non-sourced instruments. */
  inheritedFlatsByInstrumentId: Record<string, FlatLibraryQuestion[]>;
  readiness: { items: ReadinessItem[]; blocking: number; warnings: number };
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  onCollapse: () => void;
};

export function PreviewRail({
  draft,
  parsedByInstrumentId,
  sourceActivitiesByAssessmentId,
  inheritedFlatsByInstrumentId,
  readiness,
  flatByQuestionId,
  onCollapse
}: Props) {
  // Build a one-shot lookup map that includes both catalog questions and any
  // questions inherited from sourced instruments. ModuleSummary / PhoneFrame
  // resolve display metadata via this map, so they can render inherited
  // questions with no special-cased code paths.
  const augmentedFlatByQuestionId = useMemo(() => {
    const map = new Map(flatByQuestionId);
    for (const list of Object.values(inheritedFlatsByInstrumentId)) {
      for (const q of list) map.set(q.questionId, q);
    }
    return map;
  }, [flatByQuestionId, inheritedFlatsByInstrumentId]);
  const [tab, setTab] = useState<"summary" | "preview">("summary");

  const headlineColor =
    readiness.blocking > 0 ? "error.main" : readiness.warnings > 0 ? "warning.main" : "success.main";
  const headline =
    readiness.blocking > 0
      ? `${readiness.blocking} blocker${readiness.blocking === 1 ? "" : "s"}`
      : readiness.warnings > 0
        ? `${readiness.warnings} warning${readiness.warnings === 1 ? "" : "s"}`
        : "All clear";

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        position: { lg: "sticky" },
        top: { lg: 16 },
        borderRadius: 2,
        minHeight: 360,
        maxHeight: { lg: "calc(100vh - 32px)" },
        overflowY: { lg: "auto" }
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          Live preview
        </Typography>
        <Tooltip title="Hide preview">
          <IconButton size="small" onClick={onCollapse}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Readiness
        </Typography>
        <Typography variant="caption" sx={{ color: headlineColor, fontWeight: 600 }}>
          {headline}
        </Typography>
      </Stack>
      <Stack spacing={0.6} sx={{ mb: 1.5 }}>
        {readiness.items.map((item, idx) => (
          <ChecklistRow key={idx} status={item.status} text={item.text} />
        ))}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as "summary" | "preview")}
        sx={{ minHeight: 32, "& .MuiTab-root": { minHeight: 32, py: 0.25, fontSize: "0.78rem" } }}
        variant="fullWidth"
      >
        <Tab value="summary" label="Module summary" />
        <Tab value="preview" label="Assessment preview" />
      </Tabs>

      <Box sx={{ pt: 1.5 }}>
        {tab === "summary" ? (
          <ModuleSummary
            draft={draft}
            parsedByInstrumentId={parsedByInstrumentId}
            sourceActivitiesByAssessmentId={sourceActivitiesByAssessmentId}
            inheritedFlatsByInstrumentId={inheritedFlatsByInstrumentId}
            flatByQuestionId={augmentedFlatByQuestionId}
          />
        ) : (
          <AssessmentPreview
            draft={draft}
            parsedByInstrumentId={parsedByInstrumentId}
            sourceActivitiesByAssessmentId={sourceActivitiesByAssessmentId}
            inheritedFlatsByInstrumentId={inheritedFlatsByInstrumentId}
            flatByQuestionId={augmentedFlatByQuestionId}
          />
        )}
      </Box>
    </Paper>
  );
}

// Shared reuse-tint background — keep in sync with the SectionCard / Grid row
// styles so all three entities read the same visual language.
const REUSE_BG = "rgba(76, 175, 80, 0.06)";

function ReuseChip() {
  return (
    <Chip
      size="small"
      variant="outlined"
      color="success"
      label="Reuse"
      sx={{ height: 16, fontSize: "0.62rem" }}
    />
  );
}

function ChecklistRow({ status, text }: { status: ReadinessItem["status"]; text: string }) {
  const color = status === "ok" ? "success.main" : status === "warn" ? "warning.main" : "error.main";
  const dot = status === "ok" ? "✓" : status === "warn" ? "!" : "•";
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          bgcolor: color,
          color: "common.white",
          fontSize: "0.65rem",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: 0.25
        }}
      >
        {dot}
      </Box>
      <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
        {text}
      </Typography>
    </Stack>
  );
}

function ModuleSummary({
  draft,
  parsedByInstrumentId,
  sourceActivitiesByAssessmentId,
  inheritedFlatsByInstrumentId,
  flatByQuestionId
}: {
  draft: ModuleBuilderDraft;
  parsedByInstrumentId: Record<string, ParsedActivityRow[]>;
  sourceActivitiesByAssessmentId: Record<string, SourceActivity[]>;
  inheritedFlatsByInstrumentId: Record<string, FlatLibraryQuestion[]>;
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
}) {
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Module
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {draft.moduleTitle.trim() || "(no title)"}
        </Typography>
      </Box>

      {draft.instruments.map((instr) => {
        const settings = instr.assessmentSettings;
        const rows = parsedByInstrumentId[instr.id] ?? [];
        const isInstrumentSourced = Boolean(instr.sourceInstrumentId);
        const inheritedFlats = inheritedFlatsByInstrumentId[instr.id] ?? [];
        // Sourced instruments inherit their questions from the source row;
        // they are NOT in the draft's selection map. Pull the IDs (in order)
        // from the loaded detail instead.
        const qids = isInstrumentSourced
          ? inheritedFlats.map((q) => q.questionId)
          : (draft.instrumentQuestionIdsById[instr.id] ?? []);
        const requiredById = isInstrumentSourced
          ? Object.fromEntries(
              inheritedFlats.map((q) => [q.questionId, q.requiredDefault ?? true])
            )
          : (draft.instrumentQuestionRequiredById[instr.id] ?? {});
        const overridesById = isInstrumentSourced
          ? {}
          : (draft.instrumentQuestionOverridesById[instr.id] ?? {});
        const answerSetById = isInstrumentSourced
          ? {}
          : (draft.instrumentQuestionAnswerSetById[instr.id] ?? {});
        const inheritedQuestionsLoading =
          isInstrumentSourced && inheritedFlats.length === 0;
        const reusedTitlesLower = instr.sourceAssessmentId
          ? new Set(
              (sourceActivitiesByAssessmentId[instr.sourceAssessmentId] ?? []).map((a) =>
                a.title.trim().toLowerCase()
              )
            )
          : null;
        const isInstrumentReused = Boolean(instr.sourceInstrumentId);
        const isAssessmentReused = Boolean(instr.sourceAssessmentId);

        return (
          <Box
            key={instr.id}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.25,
              bgcolor: "background.default"
            }}
          >
            <Box
              sx={
                isInstrumentReused
                  ? {
                      borderLeft: "3px solid",
                      borderLeftColor: "success.main",
                      pl: 1,
                      ml: -0.5,
                      mb: 0.25,
                      bgcolor: REUSE_BG
                    }
                  : null
              }
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Instrument
                </Typography>
                {isInstrumentReused ? <ReuseChip /> : null}
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {instr.title.trim() || "(untitled)"}
              </Typography>
              {instr.description.trim() ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {instr.description}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={
                isAssessmentReused
                  ? {
                      mt: 1,
                      borderLeft: "3px solid",
                      borderLeftColor: "success.main",
                      pl: 1,
                      ml: -0.5,
                      bgcolor: REUSE_BG
                    }
                  : { mt: 1 }
              }
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Assessment
                </Typography>
                {isAssessmentReused ? <ReuseChip /> : null}
              </Stack>
              <Typography variant="body2">
                {settings.title.trim() || "(no assessment title)"}
              </Typography>
              {settings.description.trim() ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {settings.description}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Self-asmt: ${settings.requireSelfAssessment ? "Yes" : "No"}`}
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Create: ${settings.creationDurationHours}h`}
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Expire: ${settings.expirationDurationHours}h`}
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
              </Stack>
            </Box>

            <Box sx={{ mt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Activities ({rows.length})
              </Typography>
              {rows.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                  None
                </Typography>
              ) : (
                <Stack component="ol" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                  {rows.map((r, i) => {
                    const reused = Boolean(
                      reusedTitlesLower &&
                        r.title.trim() &&
                        reusedTitlesLower.has(r.title.trim().toLowerCase())
                    );
                    return (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: "0.78rem",
                          ...(reused
                            ? {
                                borderLeft: "3px solid",
                                borderLeftColor: "success.main",
                                pl: 0.75,
                                ml: -0.75,
                                bgcolor: REUSE_BG,
                                borderRadius: 0.5
                              }
                            : null)
                        }}
                      >
                        <strong>{r.title}</strong>
                        {r.description ? <> — {r.description}</> : null}
                        {r.category ? (
                          <Chip
                            label={r.category}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5, height: 16, fontSize: "0.62rem" }}
                          />
                        ) : null}
                        {reused ? (
                          <Chip
                            label="Reuse"
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{ ml: 0.5, height: 16, fontSize: "0.62rem" }}
                          />
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Box sx={{ mt: 1.25 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Questions ({qids.length})
                </Typography>
                {isInstrumentSourced && qids.length > 0 ? <ReuseChip /> : null}
              </Stack>
              {inheritedQuestionsLoading ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Loading inherited questions…
                </Typography>
              ) : qids.length === 0 ? (
                <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                  None
                </Typography>
              ) : (
                <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                  {qids.map((qid, idx) => {
                    const ov = overridesById[qid];
                    const meta = getQuestionDisplayMeta(
                      qid,
                      flatByQuestionId,
                      ov ? { [qid]: ov } : {},
                      answerSetById[qid]
                    );
                    const required = requiredById[qid] ?? true;
                    const optsText =
                      ov?.optionsText !== undefined
                        ? ov.optionsText
                        : answersToOptionsText(
                            meta.answers.map((a) => ({ label: a.label, value: a.value }))
                          );
                    const opts =
                      meta.kind === "feedback"
                        ? []
                        : ov?.optionsText !== undefined
                          ? parseOptionsText(ov.optionsText)
                          : meta.answers.map((a) => ({ label: a.label, value: a.value }));
                    return (
                      <Box component="li" key={qid} sx={{ fontSize: "0.78rem" }}>
                        <Box>
                          <strong>{meta.title || `Question ${idx + 1}`}</strong>{" "}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              meta.kind === "feedback"
                                ? "Feedback"
                                : meta.kind === "multi"
                                  ? "Multi"
                                  : "Radio"
                            }
                            sx={{ height: 16, fontSize: "0.62rem", ml: 0.25 }}
                          />
                          {required ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Required"
                              sx={{ height: 16, fontSize: "0.62rem", ml: 0.25 }}
                            />
                          ) : null}
                          {qid.startsWith("custom-") ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="Custom"
                              sx={{ height: 16, fontSize: "0.62rem", ml: 0.25 }}
                            />
                          ) : null}
                        </Box>
                        {meta.prompt ? (
                          <Box sx={{ color: "text.secondary" }}>Prompt: {meta.prompt}</Box>
                        ) : null}
                        {ov?.selfAssessmentPrompt?.trim() ? (
                          <Box sx={{ color: "text.secondary" }}>
                            Self-asmt: {ov.selfAssessmentPrompt}
                          </Box>
                        ) : null}
                        {ov?.helpText?.trim() ? (
                          <Box sx={{ color: "text.secondary" }}>Help: {ov.helpText}</Box>
                        ) : null}
                        {opts.length > 0 ? (
                          <Box sx={{ color: "text.secondary" }}>
                            Options: {opts.map((o) => o.label).join(", ")}
                          </Box>
                        ) : null}
                        {void optsText}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function AssessmentPreview({
  draft,
  parsedByInstrumentId,
  sourceActivitiesByAssessmentId,
  inheritedFlatsByInstrumentId,
  flatByQuestionId
}: {
  draft: ModuleBuilderDraft;
  parsedByInstrumentId: Record<string, ParsedActivityRow[]>;
  sourceActivitiesByAssessmentId: Record<string, SourceActivity[]>;
  inheritedFlatsByInstrumentId: Record<string, FlatLibraryQuestion[]>;
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
}) {
  const flowOptions = useMemo(() => {
    const out: {
      instrumentId: string;
      instrumentTitle: string;
      activityIndex: number;
      activityTitle: string;
      activityCategory: string;
      activityDescription: string;
      reused: boolean;
    }[] = [];
    for (const instr of draft.instruments) {
      const rows = parsedByInstrumentId[instr.id] ?? [];
      const reusedTitles = instr.sourceAssessmentId
        ? new Set(
            (sourceActivitiesByAssessmentId[instr.sourceAssessmentId] ?? []).map((a) =>
              a.title.trim().toLowerCase()
            )
          )
        : null;
      rows.forEach((row, idx) => {
        const reused = Boolean(
          reusedTitles &&
            row.title.trim() &&
            reusedTitles.has(row.title.trim().toLowerCase())
        );
        out.push({
          instrumentId: instr.id,
          instrumentTitle: instr.title || "Instrument",
          activityIndex: idx,
          activityTitle: row.title || `Activity ${idx + 1}`,
          activityCategory: row.category,
          activityDescription: row.description,
          reused
        });
      });
    }
    return out;
  }, [draft.instruments, parsedByInstrumentId, sourceActivitiesByAssessmentId]);

  const [selectedKey, setSelectedKey] = useState<string>("");
  const effectiveKey =
    selectedKey && flowOptions.find((f) => key(f.instrumentId, f.activityIndex) === selectedKey)
      ? selectedKey
      : flowOptions[0]
        ? key(flowOptions[0].instrumentId, flowOptions[0].activityIndex)
        : "";
  const selected = flowOptions.find(
    (f) => key(f.instrumentId, f.activityIndex) === effectiveKey
  );

  if (flowOptions.length === 0) {
    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 1.25,
          color: "text.secondary",
          fontSize: "0.8125rem",
          textAlign: "center"
        }}
      >
        Add an activity to see the assessment view.
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <Select
        size="small"
        fullWidth
        value={effectiveKey}
        onChange={(e) => setSelectedKey(String(e.target.value))}
      >
        {flowOptions.map((f) => (
          <MenuItem
            key={key(f.instrumentId, f.activityIndex)}
            value={key(f.instrumentId, f.activityIndex)}
          >
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                component="span"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {f.instrumentTitle} — {f.activityTitle}
              </Box>
              {f.reused ? <ReuseChip /> : null}
            </Stack>
          </MenuItem>
        ))}
      </Select>

      {selected
        ? (() => {
            const selectedInstrument = draft.instruments.find(
              (i) => i.id === selected.instrumentId
            );
            const isSourced = Boolean(selectedInstrument?.sourceInstrumentId);
            const inheritedFlats = inheritedFlatsByInstrumentId[selected.instrumentId] ?? [];
            const phoneQids = isSourced
              ? inheritedFlats.map((q) => q.questionId)
              : (draft.instrumentQuestionIdsById[selected.instrumentId] ?? []);
            const phoneOverrides = isSourced
              ? {}
              : (draft.instrumentQuestionOverridesById[selected.instrumentId] ?? {});
            const phoneAnswerSets = isSourced
              ? {}
              : (draft.instrumentQuestionAnswerSetById[selected.instrumentId] ?? {});
            const inheritedLoading = isSourced && inheritedFlats.length === 0;
            return (
              <PhoneFrame
                activityTitle={selected.activityTitle}
                activityCategory={selected.activityCategory}
                activityDescription={selected.activityDescription}
                activityReused={selected.reused}
                instrumentTitle={selected.instrumentTitle}
                questionIds={phoneQids}
                overridesById={phoneOverrides}
                answerSetById={phoneAnswerSets}
                flatByQuestionId={flatByQuestionId}
                inheritedLoading={inheritedLoading}
              />
            );
          })()
        : null}
    </Stack>
  );
}

function key(instrumentId: string, activityIndex: number): string {
  return `${instrumentId}::${activityIndex}`;
}

function PhoneFrame({
  activityTitle,
  activityCategory,
  activityDescription,
  activityReused,
  instrumentTitle,
  questionIds,
  overridesById,
  answerSetById,
  flatByQuestionId,
  inheritedLoading
}: {
  activityTitle: string;
  activityCategory: string;
  activityDescription: string;
  activityReused: boolean;
  instrumentTitle: string;
  questionIds: string[];
  overridesById: Record<
    string,
    { title?: string; prompt?: string; optionsText?: string; type?: "radio" | "multi" | "feedback"; helpText?: string; selfAssessmentPrompt?: string }
  >;
  answerSetById: Record<string, string>;
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  inheritedLoading: boolean;
}) {
  return (
    <Box
      sx={{
        border: "10px solid",
        borderColor: "grey.400",
        borderRadius: 3,
        bgcolor: "background.paper",
        overflow: "hidden",
        boxShadow: 1
      }}
    >
      <Box
        sx={{
          bgcolor: activityReused ? REUSE_BG : "grey.100",
          px: 1.25,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          ...(activityReused
            ? { borderLeft: "3px solid", borderLeftColor: "success.main" }
            : null)
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.1 }}>
          {instrumentTitle}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {activityTitle}
          </Typography>
          {activityCategory ? (
            <Chip
              label={activityCategory}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
          ) : null}
          {activityReused ? <ReuseChip /> : null}
        </Stack>
        {activityDescription ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {activityDescription}
          </Typography>
        ) : null}
      </Box>
      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {inheritedLoading ? (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            Loading inherited questions…
          </Typography>
        ) : questionIds.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            No questions added yet.
          </Typography>
        ) : (
          questionIds.map((qid, idx) => {
            const ov = overridesById[qid];
            const meta = getQuestionDisplayMeta(
              qid,
              flatByQuestionId,
              ov ? { [qid]: ov } : {},
              answerSetById[qid]
            );
            return (
              <Box key={qid}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  {idx + 1}. {meta.title}
                </Typography>
                {meta.prompt ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {meta.prompt}
                  </Typography>
                ) : null}
                {ov?.helpText?.trim() ? (
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontStyle: "italic" }}>
                    {ov.helpText}
                  </Typography>
                ) : null}
                {meta.kind === "feedback" ? (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0.75,
                      color: "text.secondary",
                      fontSize: "0.75rem"
                    }}
                  >
                    Free text response
                  </Box>
                ) : meta.answers.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">
                    No options yet
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {meta.answers.map((a, i) => (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        key={`${a.label}-${i}`}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: meta.kind === "multi" ? 0.5 : "50%",
                            border: "1.5px solid",
                            borderColor: "grey.500",
                            flexShrink: 0
                          }}
                        />
                        <Box>{a.label}</Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
